import currency from "currency.js";
import { apiNum } from "./math";

/**
 * Display helpers for numbers the platform may not know.
 *
 * The backend now sends `null` for a position's `mark_price`, `unrealized_pnl`
 * and everything derived from them when it cannot resolve the market, and a
 * ticker can arrive without a mark. `Number(null)` is 0 and `currency(null)` is
 * "$0.00", so every unguarded read turned "unknown" into a confident zero. These
 * helpers keep unknown as unknown: "-" on screen (the marker the rest of the app
 * already uses), `undefined` in arithmetic.
 */

/** What an unknown number renders as. */
export const UNKNOWN_DISPLAY = "-";

/**
 * A price that was actually quoted: finite and positive. No perp, index or
 * spot market trades at $0, so zero is "not known", exactly as in the trading
 * header.
 */
export const knownPrice = (
  value?: string | number | null
): number | undefined => {
  const n = apiNum(value);
  return n !== undefined && n > 0 ? n : undefined;
};

/**
 * The sum of values that are ALL known, else `undefined`. A total that silently
 * skips (or zeroes) an unknown leg is a wrong total that looks right.
 */
export const sumKnown = (
  values: ReadonlyArray<string | number | null | undefined>
): number | undefined => {
  let total = 0;
  for (let i = 0; i < values.length; i += 1) {
    const n = apiNum(values[i]);
    if (n === undefined) return undefined;
    total += n;
  }
  return total;
};

/** A quoted price as currency, or "-". */
export const formatKnownPrice = (
  value?: string | number | null,
  precision?: number
): string => {
  const n = knownPrice(value);
  if (n === undefined) return UNKNOWN_DISPLAY;
  return precision === undefined
    ? currency(n).format()
    : currency(n, { precision }).format();
};

/** A known amount (zero allowed) as currency, or "-". */
export const formatKnownValue = (
  value?: string | number | null,
  precision?: number
): string => {
  const n = apiNum(value);
  if (n === undefined) return UNKNOWN_DISPLAY;
  return precision === undefined
    ? currency(n).format()
    : currency(n, { precision }).format();
};

/** A fractional ROI (0.25 = +25%) as a signed percentage, or "-". */
export const formatRoiPercent = (roi?: string | number | null): string => {
  const n = apiNum(roi);
  if (n === undefined) return UNKNOWN_DISPLAY;
  return `${n >= 0 ? "+" : ""}${(n * 100).toFixed(2)}%`;
};

/**
 * Up / down colour for a signed value, and a neutral colour when it is unknown.
 * `>= 0 ? up : down` painted an unknown (NaN) red, and `Number(null) >= 0`
 * painted it green.
 */
export const signedTone = <T>(
  value: string | number | null | undefined,
  tones: { up: T; down: T; unknown: T }
): T => {
  const n = apiNum(value);
  if (n === undefined) return tones.unknown;
  return n >= 0 ? tones.up : tones.down;
};

/**
 * Sort comparator for possibly-unknown numbers: numeric order, with unknowns
 * grouped together below every known value — never ranked as if they were 0.
 */
export const compareKnownNumbers = (
  a: string | number | null | undefined,
  b: string | number | null | undefined
): number => {
  const x = apiNum(a);
  const y = apiNum(b);
  if (x === undefined && y === undefined) return 0;
  if (x === undefined) return -1;
  if (y === undefined) return 1;
  if (x === y) return 0;
  return x > y ? 1 : -1;
};

/** react-table `sortType` built on {@link compareKnownNumbers}. */
export const knownNumberSortType = (
  rowA: { values: Record<string, any> },
  rowB: { values: Record<string, any> },
  columnId: string
): number => compareKnownNumbers(rowA.values[columnId], rowB.values[columnId]);

/**
 * A 24h change as a fraction of the price 24h ago, or `undefined`. It needs a
 * real reference price: deriving one as `price - change` always "succeeded"
 * (and mixed index with mark), and an absent change is not a flat 0%.
 */
export const dailyChangeFraction = (
  change?: string | number | null,
  price24hAgo?: string | number | null
): number | undefined => {
  const c = apiNum(change);
  const reference = knownPrice(price24hAgo);
  return c !== undefined && reference !== undefined ? c / reference : undefined;
};
